/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState } from "react";
import {
  ContextualSaveBar,
  BlockStack,
  Box,
  Card,
  FormLayout,
  Layout,
  Loading,
  Page,
  TextField,
  Text,
  SkeletonDisplayText,
  SkeletonBodyText,
  Badge,
} from "@medentic/polaris-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import CompanyProfilePicture from "./CompanyProfilePicture";
import CompanyBillingDetails from "./CompanyBillingDetails";
import { PhoneNumberField } from "@shared/ui";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { Timezone } from "@shared/types/commonTypes";
import { TimezoneAutoComplete } from "@shared/ui";
import { WebsiteField } from "@shared/ui";

interface CompanyDetailsPageProps {
  companyId: string;
}

const CompanyDetailsPage: React.FC<CompanyDetailsPageProps> = ({
  companyId,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { PUT } = useApiRequest();

  const [businessName, setBusinessName] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [customerEmail, setCustomerEmail] = useState<string>("");
  const [customerPhoneCountryCode, setCustomerPhoneCountryCode] =
    useState<string>("");
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState<string>("");
  const [timezone, setTimezone] = useState<Timezone | null>(null);
  const [taxNumber, setTaxNumber] = useState<string>("");
  const [taxType, setTaxType] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [apartment, setApartment] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [provinceCode, setProvinceCode] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [sendInvoices, setSendInvoices] = useState<boolean>(false);
  const [billingEmails, setBillingEmails] = useState<string>("");
  const [invoiceLanguageCode, setInvoiceLanguageCode] = useState<string>("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});

  const { 
    company: companyData, 
    isLoading: isCompanyLoading, 
    refetchCompany 
  } = useGetSingleCompanyDetails(companyId, {
    refetchOnMount: true, // Always refetch when component mounts to get latest data
  });

  useEffect(() => {
    if (companyData) {
      setName(companyData.name);
      setBusinessName(companyData.businessName || "");
      setWebsite(companyData.website || "");
      setCustomerEmail(companyData.customerEmail);
      setCustomerPhoneCountryCode(companyData.customerPhoneCountryCode || "");
      setCustomerPhoneNumber(companyData.customerPhone || "");
      setTimezone(companyData.timezone || null);
      setTaxNumber(companyData.taxNumber);
      setTaxType(companyData.taxType);
      setAddress(companyData.address?.address1 || "");
      setApartment(companyData.address?.address2 || "");
      setCity(companyData.address?.city || "");
      setProvinceCode(companyData.address?.provinceCode || "");
      setCountry(companyData.address?.countryISO3 || "");
      setSendInvoices(companyData.sendInvoices || false);
      setBillingEmails(companyData.billingEmails || "");
      setInvoiceLanguageCode(companyData.invoiceLanguageCode || "");
    }
  }, [companyData]);

  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => {
    setter(value);
    setHasUnsavedChanges(true);
  };

  const handleInputChangeWithApiErrorClear = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    fieldName: string
  ) => {
    setter(value);
    setHasUnsavedChanges(true);
    // Clear API error for this field when user starts typing
    if (apiErrors[fieldName]) {
      setApiErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleBooleanChange = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    value: boolean
  ) => {
    setter(value);
    setHasUnsavedChanges(true);
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};
    if (!name) newErrors.name = true;
    if (!businessName) newErrors.businessName = true; // Legal business name validation
    if (!customerPhoneCountryCode) newErrors.customerPhoneCountryCode = true;
    if (!address) newErrors.address = true;
    if (!city) newErrors.city = true;
    if (!taxNumber) newErrors.taxNumber = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateCompany = useMutation({
    mutationFn: async (
      updatedData: Record<string, string | boolean | null | undefined>
    ) => {
      try {
        return await PUT(`/api/healthcare-companies/${companyId}`, updatedData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        // Debug: Alert the error structure for inline errors
        if (error?.response?.data?.error?.inline) {
          // Handle inline validation errors
          setApiErrors({
            [error.response.data.error.inline]:
              error.response.data.error.display,
          });
        }

        // Re-throw the error so useApiRequest.ts can handle toast logic
        throw error;
      }
    },
    onSuccess: () => {
      // Refetch the company data to get the latest information
      refetchCompany();
      // Also invalidate the healthcare-companies list to keep it in sync
      queryClient.invalidateQueries({ queryKey: ["healthcare-companies"] });
      setHasUnsavedChanges(false);
      setApiErrors({}); // Clear API errors on success
    },
  });

  const handleDiscard = () => {
    setHasUnsavedChanges(false);
    setErrors({});
    setApiErrors({});
    setName(companyData?.name ?? "");
    setBusinessName(companyData?.businessName ?? "");
    setWebsite(companyData?.website ?? "");
    setCustomerEmail(companyData?.customerEmail ?? "");
    setCustomerPhoneCountryCode(companyData?.customerPhoneCountryCode ?? "");
    setCustomerPhoneNumber(companyData?.customerPhone ?? "");
    setTimezone(companyData?.timezone ?? null);
    setTaxNumber(companyData?.taxNumber ?? "");
    setTaxType(companyData?.taxType ?? "");
    setAddress(companyData?.address?.address1 ?? "");
    setApartment(companyData?.address?.address2 ?? "");
    setCity(companyData?.address?.city ?? "");
    setProvinceCode(companyData?.address?.provinceCode ?? "");
    setCountry(companyData?.address?.countryISO3 ?? "");
    setSendInvoices(companyData?.sendInvoices ?? false);
    setBillingEmails(companyData?.billingEmails ?? "");
    setInvoiceLanguageCode(companyData?.invoiceLanguageCode ?? "");
  };

  const handleSave = () => {
    if (!validateFields()) {
      return;
    }

    if (hasUnsavedChanges) {
      updateCompany.mutate({
        name,
        businessName,
        website,
        customerEmail,
        customerPhoneCountryCode,
        customerPhoneNumber,
        timezone: timezone?.id,
        taxNumber,
        taxType,
        address1: address,
        address2: apartment,
        city,
        provinceCode: provinceCode,
        countryISO3: country,
        sendInvoices,
        billingEmails,
        invoiceLanguageCode,
      });
    }
  };

  const renderSkeleton = () => {
    return (
      <BlockStack gap={"400"}>
        <BlockStack gap={"200"}>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
        <BlockStack gap={"200"}>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
        <BlockStack gap={"200"}>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </BlockStack>
    );
  };

  return (
    <Page
      title={t("companyDetails.details")}
      titleMetadata={companyData?.isTest ? <Badge tone="warning">{t("common.test")}</Badge> : undefined}
      narrowWidth>
      {isCompanyLoading ? <Loading /> : null}
      {hasUnsavedChanges && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            onAction: handleSave,
            loading: updateCompany.isPending,
          }}
          discardAction={{
            onAction: handleDiscard,
          }}
        />
      )}
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text as="h1" variant="headingMd">
                  {t("companyDetails.companyLogo")}
                </Text>

                <CompanyProfilePicture
                  loading={isCompanyLoading}
                  logoUrl={companyData?.avatar?.image ?? ""}
                  companyId={companyId}
                />
              </BlockStack>

              <FormLayout>
                <BlockStack gap="100">
                  <Text as="h1" variant="headingMd">
                    {t("companyDetails.details")}
                  </Text>
                  <Text as="p" tone="subdued">
                    {t("companyDetails.detailsDescription")}
                  </Text>
                </BlockStack>
                {isCompanyLoading ? (
                  renderSkeleton()
                ) : (
                  <BlockStack gap="400">
                    <FormLayout.Group>
                      <TextField
                        label={t("companyDetails.companyName")}
                        value={name}
                        onChange={(value) =>
                          handleInputChange(setName, value)
                        }
                        autoComplete="off"
                        requiredIndicator
                        error={
                          errors.name &&
                          t("common.requiredField", {
                            field_name: t("companyDetails.companyName"),
                          })
                        }
                      />
                      <WebsiteField
                        label={t("companyDetails.companyWebsite")}
                        value={website}
                        onChange={(value) =>
                          handleInputChange(setWebsite, value)
                        }
                      />
                    </FormLayout.Group>

                    <FormLayout.Group>
                      <PhoneNumberField
                        value={
                          customerPhoneCountryCode + customerPhoneNumber
                        }
                        onChange={(value) => {
                          try {
                            const phoneObj =
                              parsePhoneNumberWithError(value);
                            if (phoneObj) {
                              const cc = `+${phoneObj.countryCallingCode}`;
                              const ln = phoneObj.nationalNumber;
                              handleInputChange(
                                setCustomerPhoneCountryCode,
                                cc
                              );
                              handleInputChange(setCustomerPhoneNumber, ln);
                            }
                          } catch (err) {
                            console.log(err);
                          }
                        }}
                        phoneLabel={t("companyDetails.companyPhone")}
                        phoneError={
                          errors.customerPhoneNumber
                            ? t("common.requiredField", {
                                field_name: t(
                                  "companyDetails.companyPhone"
                                ),
                              })
                            : ""
                        }
                        disabled={false}
                        isRequired={false}
                      />

                      <TextField
                        label={t("companyDetails.companyEmail")}
                        value={customerEmail}
                        onChange={(value) =>
                          handleInputChange(setCustomerEmail, value)
                        }
                        autoComplete="email"
                      />
                    </FormLayout.Group>

                    <TimezoneAutoComplete
                      label={t("companyDetails.timezone")}
                      value={timezone}
                      onChange={(tz: Timezone | null) => {
                        setTimezone(tz);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </BlockStack>
                )}
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>
        <CompanyBillingDetails
          verifiedBusinessAt={companyData?.verifiedBusinessAt}
          businessName={businessName}
          businessMeta={companyData?.billingMeta ?? null}
          taxNumber={taxNumber}
          taxType={taxType}
          address={address}
          apartment={apartment}
          city={city}
          provinceCode={provinceCode}
          country={country}
          sendInvoices={sendInvoices}
          billingEmails={billingEmails}
          invoiceLanguageCode={invoiceLanguageCode}
          isLoading={isCompanyLoading}
          errors={errors}
          apiErrors={apiErrors}
          handleInputChange={handleInputChange}
          handleInputChangeWithApiErrorClear={
            handleInputChangeWithApiErrorClear
          }
          handleBooleanChange={handleBooleanChange}
          setBusinessName={setBusinessName}
          setTaxNumber={setTaxNumber}
          setTaxType={setTaxType}
          setAddress={setAddress}
          setApartment={setApartment}
          setCity={setCity}
          setProvinceCode={setProvinceCode}
          setCountry={setCountry}
          setSendInvoices={setSendInvoices}
          setBillingEmails={setBillingEmails}
          setInvoiceLanguageCode={setInvoiceLanguageCode}
        />
      </Layout>
      <Box paddingBlock="200" />
    </Page>
  );
};

export default CompanyDetailsPage;
